import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { Coverage, Organization } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

export type HealthInsuranceResponse = {
  insurance: [
    {
      coverage: Coverage;
      payor: Organization;
    },
  ];
};

export type BillingInsuranceResponse = {
  insurance: {
    payer: {
      id: string;
      name: string;
      availityEligibilityId?: string;
      candidId?: string;
      availityClaimsPayerId?: string;
    };
    memberId: string;
    relationshipToInsured: string;
    needsVerification?: boolean;
    planName?: string;
    groupNumber?: string;
    customerServicePhoneNumber?: string;
  } | null;
};

export type HealthInsuranceCoverage = {
  coverageId: string | undefined;
  payorOrganizationName: string | undefined;
  payorOrganizationId: string | undefined;
  memberIdentifier: string | undefined;
  planCode?: string;
  groupNumber?: string;
  customerServicePhoneNumber?: string;
  subscriberRelationship?: "self" | "spouse" | "child" | "other";
};

export type PayorsResponse = {
  payors: Organization[];
};

export type BillingInsuranceCarrier = {
  id: string;
  displayName: string;
};

export type CreateInsuranceCoverageRequest = {
  patientId: string;
  payorOrgId: string;
  memberId: string;
  planCode?: string;
  groupNumber?: string;
  customerServicePhone?: string;
  subscriberRelationship?: string;
};

export type CreateBillingInsuranceCoverageRequest = {
  carrierId: string;
  memberId: string;
  patientRelationship: "self" | "spouse" | "child" | "other";
  planName: string | undefined;
  groupNumber: string | undefined;
  customerServicePhone: string | undefined;
};

export type CreateInsuranceCoverageResponse = {
  coverage: Coverage;
};

export type CancelInsuranceCoverageParams = {
  coverageId: string;
};

export type CancelInsuranceCoverageResponse = {
  coverage: Coverage;
};

export const useHealthInsuranceCoverage = (
  patientId?: string,
  options?: { enabled?: boolean },
) => {
  return useApi<HealthInsuranceResponse, HealthInsuranceCoverage[]>({
    queryOptions: {
      enabled: options?.enabled ?? Boolean(patientId),
      queryKey:
        QueryKeys.Patient.MedicalRecords.HealthInsurance.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/clinic/insurance/coverage", {
          params: { patientId },
        });
        return response?.data;
      },
      select: (data): HealthInsuranceCoverage[] => {
        return data?.insurance.map((insurance) => {
          const coverage = new Coverage(insurance.coverage);
          const payor = new Organization(insurance.payor);

          return {
            coverageId: insurance.coverage.id,
            payorOrganizationName: payor.name,
            payorOrganizationId: payor.id,
            memberIdentifier: coverage.subscriberId,
            planCode: coverage.planCode,
            groupNumber: coverage.groupNumber,
            customerServicePhoneNumber: coverage.customerServicePhoneNumber,
            subscriberRelationship: coverage.subscriberRelationship as
              | "self"
              | "spouse"
              | "child"
              | "other"
              | undefined,
          };
        });
      },
    },
  });
};

export const useBillingInsuranceCoverage = (
  patientId?: string,
  options?: { enabled?: boolean },
) => {
  return useApi<
    BillingInsuranceResponse,
    HealthInsuranceCoverage[] | undefined
  >({
    queryOptions: {
      enabled: options?.enabled ?? Boolean(patientId),
      queryKey: ["patient", "health-insurance", "billing", patientId],
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/billing/patient/${patientId}`,
        );
        return response?.data;
      },
      select: (data): HealthInsuranceCoverage[] | undefined => {
        if (!data?.insurance) {
          return undefined;
        }
        const insurance = data?.insurance;
        const relationship = insurance.relationshipToInsured;

        return [
          {
            coverageId: insurance.payer?.id ?? "billing-coverage",
            payorOrganizationName: insurance.payer?.name ?? "",
            payorOrganizationId: insurance.payer?.id ?? "",
            memberIdentifier: insurance.memberId,
            planCode: insurance.planName,
            groupNumber: insurance.groupNumber,
            customerServicePhoneNumber: insurance.customerServicePhoneNumber,
            subscriberRelationship:
              (relationship as
                | "self"
                | "spouse"
                | "child"
                | "other"
                | undefined) ?? undefined,
          },
        ];
      },
    },
  });
};

export const useCreateInsuranceCoverage = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const clinicCoverageKey =
    QueryKeys.Patient.MedicalRecords.HealthInsurance.DEFAULT(patientId);
  const billingCoverageKey = [
    "patient",
    "health-insurance",
    "billing",
    patientId,
  ];

  const postCoverage = async (data: CreateInsuranceCoverageRequest) => {
    const response = await axiosInstance.post("/clinic/insurance/coverage", {
      patientId: data.patientId,
      memberId: data.memberId,
      planCode: data.planCode,
      groupNumber: data.groupNumber,
      customerServicePhone: data.customerServicePhone,
      payorOrgId: data.payorOrgId,
      subscriberRelationship: data.subscriberRelationship,
    });
    return response.data;
  };

  return useApiMutation<
    CreateInsuranceCoverageResponse,
    CreateInsuranceCoverageRequest
  >({
    mutationOptions: {
      mutationKey: ["patient", "health-insurance", "create"],
      mutationFn: async (data) => {
        return postCoverage(data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: clinicCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: billingCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.ORDER_ENTRY_ACTIONS({
            patientId,
          }),
        });
        onSuccess?.();
      },
    },
  });
};

export const useBillingHealthInsurance = (
  patientId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();
  const clinicCoverageKey =
    QueryKeys.Patient.MedicalRecords.HealthInsurance.DEFAULT(patientId);
  const billingCoverageKey = [
    "patient",
    "health-insurance",
    "billing",
    patientId,
  ];

  const postInsurance = async (data: CreateBillingInsuranceCoverageRequest) => {
    const response = await axiosInstance.post(
      `/billing/patient/${patientId}/insurance`,
      {
        id: data.carrierId,
        memberId: data.memberId,
        patientRelationshipToInsured: data.patientRelationship,
        planName: data.planName,
        groupNumber: data.groupNumber,
        customerServicePhoneNumber: data.customerServicePhone,
      },
    );
    return response.data;
  };

  return useApiMutation<void, CreateBillingInsuranceCoverageRequest>({
    mutationOptions: {
      mutationKey: ["patient", "health-insurance", "create"],
      mutationFn: async (data) => {
        return postInsurance(data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: clinicCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: billingCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.ORDER_ENTRY_ACTIONS({
            patientId,
          }),
        });
        onSuccess?.();
      },
    },
  });
};

export const useHealthInsurancePayors = (options?: { enabled?: boolean }) => {
  return useApi<PayorsResponse, Organization[]>({
    queryOptions: {
      enabled: options?.enabled ?? true,
      queryKey: QueryKeys.Patient.MedicalRecords.HealthInsurance.PAYORS,
      queryFn: async () => {
        const response = await axiosInstance.get("/clinic/insurance/payors");
        return response?.data;
      },
      select: (data): Organization[] => {
        return (
          data?.payors?.map((organization) => new Organization(organization)) ||
          []
        );
      },
    },
  });
};

export const useBillingCancelInsurance = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const clinicCoverageKey =
    QueryKeys.Patient.MedicalRecords.HealthInsurance.DEFAULT(patientId);
  const billingCoverageKey = [
    "patient",
    "health-insurance",
    "billing",
    patientId,
  ];

  const deleteInsurance = async () => {
    const response = await axiosInstance.delete(
      `/billing/patient/${patientId}/insurance`,
    );
    return response.data;
  };

  return useApiMutation<void, void>({
    mutationOptions: {
      mutationKey: ["patient", "health-insurance", "delete", "billing"],
      mutationFn: async () => {
        return deleteInsurance();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: clinicCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: billingCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.ORDER_ENTRY_ACTIONS({
            patientId,
          }),
        });
        onSuccess?.();
      },
    },
  });
};

export const useBillingInsuranceCarriers = (options?: {
  enabled?: boolean;
}) => {
  return useApi<BillingInsuranceCarrier[]>({
    queryOptions: {
      enabled: options?.enabled ?? true,
      queryKey: QueryKeys.Patient.MedicalRecords.HealthInsurance.CARRIERS,
      queryFn: async () => {
        const response = await axiosInstance.get("/billing/insurance/payers");
        return response?.data;
      },
    },
  });
};

export const useCancelCoverage = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const clinicCoverageKey =
    QueryKeys.Patient.MedicalRecords.HealthInsurance.DEFAULT(patientId);
  const billingCoverageKey = [
    "patient",
    "health-insurance",
    "billing",
    patientId,
  ];

  const postCoverage = async ({ coverageId }: { coverageId: string }) => {
    const response = await axiosInstance.post(
      `/clinic/insurance/coverage/${coverageId}/cancel`,
    );
    return response.data;
  };

  return useApiMutation<
    CancelInsuranceCoverageResponse,
    CancelInsuranceCoverageParams
  >({
    mutationOptions: {
      mutationKey: ["patient", "health-insurance", "cancel"],
      mutationFn: async (data) => {
        return postCoverage(data);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: clinicCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: billingCoverageKey,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Labs.ORDER_ENTRY_ACTIONS({
            patientId,
          }),
        });
        onSuccess?.();
      },
    },
  });
};
